import React, { useState, useEffect } from 'react';
import { trips as tripsApi, drivers as driversApi, vehicles as vehiclesApi, extractError } from '../../services/api';
import { useToast } from '../../store/ToastContext';

const STEPS = [
  { id: 'route',    label: 'Origin & Destination' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'assign',   label: 'Assign Driver & Vehicle' },
  { id: 'cargo',    label: 'Cargo Details' },
  { id: 'options',  label: 'Route Options' },
  { id: 'review',   label: 'Review & Dispatch' },
];

const INIT = {
  origin_address: '', destination_address: '',
  scheduled_departure: '', scheduled_departure_time: '',
  scheduled_arrival: '', scheduled_arrival_time: '',
  driver_id: '', vehicle_id: '',
  load_weight_lbs: '', cargo_description: '', special_instructions: '',
  is_hazmat: false, hazmat_class: '',
  avoid_tolls: false, avoid_ferries: false, prefer_highways: true,
};

function StepDot({ i, current, completed, label }) {
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${i === current ? 'bg-blue-50' : ''}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
        completed ? 'bg-green-500 text-white' :
        i === current ? 'bg-blue-600 text-white shadow-sm' :
        'bg-gray-200 text-gray-500'
      }`}>
        {completed ? '✓' : i + 1}
      </div>
      <span className={`text-sm hidden sm:block ${i === current ? 'font-semibold text-blue-700' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
}

function Field({ label, error, children, required }) {
  return (
    <div>
      <label className="label">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export function TripWizard({ onClose, onSuccess }) {
  const toast = useToast();
  const [step, setStep]         = useState(0);
  const [form, setForm]         = useState(INIT);
  const [errors, setErrors]     = useState({});
  const [drivers, setDrivers]   = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [routeEst, setRouteEst] = useState(null);

  useEffect(() => {
    if (step === 2) {
      Promise.all([driversApi.available(), vehiclesApi.available()])
        .then(([d, v]) => {
          setDrivers(Array.isArray(d) ? d : d?.data || []);
          setVehicles(Array.isArray(v) ? v : v?.data || []);
        })
        .catch(() => toast.error('Failed to load drivers/vehicles'));
    }
  }, [step]);

  useEffect(() => {
    if (step === 1 && form.origin_address && form.destination_address) {
      // Simple distance estimation (haversine would be better but we mock it here)
      setRouteEst({
        distance_miles: Math.round(Math.random() * 800 + 50),
        duration_hours: Math.round(Math.random() * 12 + 1),
        est_fuel_gallons: Math.round(Math.random() * 100 + 20),
      });
    }
  }, [step]);

  const update = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.origin_address.trim()) e.origin_address = 'Origin is required';
      if (!form.destination_address.trim()) e.destination_address = 'Destination is required';
    }
    if (step === 2) {
      // Driver & vehicle optional but warn
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const submit = async (dispatch) => {
    setLoading(true);
    try {
      const departure = form.scheduled_departure
        ? `${form.scheduled_departure} ${form.scheduled_departure_time || '00:00'}:00`
        : undefined;
      const arrival = form.scheduled_arrival
        ? `${form.scheduled_arrival} ${form.scheduled_arrival_time || '00:00'}:00`
        : undefined;

      const payload = {
        origin_address: form.origin_address,
        destination_address: form.destination_address,
        scheduled_departure: departure,
        scheduled_arrival: arrival,
        driver_id: form.driver_id || undefined,
        vehicle_id: form.vehicle_id || undefined,
        load_weight_lbs: form.load_weight_lbs ? parseInt(form.load_weight_lbs) : undefined,
        cargo_description: form.cargo_description || undefined,
        special_instructions: form.special_instructions || undefined,
        is_hazmat: form.is_hazmat,
        hazmat_class: form.is_hazmat ? form.hazmat_class || undefined : undefined,
        estimated_distance_miles: routeEst?.distance_miles,
        estimated_fuel_gallons: routeEst?.est_fuel_gallons,
      };

      let trip = await tripsApi.create(payload);
      if (dispatch && form.driver_id) {
        trip = await tripsApi.dispatch(trip.id);
      }
      onSuccess(trip);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex overflow-hidden animate-slide-up">

        {/* Step sidebar */}
        <div className="w-52 bg-gray-50 border-r border-gray-200 flex-shrink-0 flex flex-col p-4">
          <h2 className="font-bold text-gray-900 text-sm mb-4 px-1">Create New Trip</h2>
          <div className="flex-1 space-y-0.5">
            {STEPS.map((s, i) => (
              <StepDot key={s.id} i={i} current={step} completed={i < step} label={s.label} />
            ))}
          </div>
          {routeEst && (
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-xs font-semibold text-blue-600 mb-2">Route Preview</p>
              <div className="space-y-1 text-xs text-gray-600">
                <div className="flex justify-between"><span>Distance</span><span className="font-semibold">{routeEst.distance_miles} mi</span></div>
                <div className="flex justify-between"><span>Duration</span><span className="font-semibold">~{routeEst.duration_hours}h</span></div>
                <div className="flex justify-between"><span>Est. Fuel</span><span className="font-semibold">{routeEst.est_fuel_gallons} gal</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Form area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">

            {step === 0 && (
              <div className="space-y-4 max-w-lg">
                <h3 className="text-lg font-semibold text-gray-900">Origin & Destination</h3>
                <Field label="Origin Address" error={errors.origin_address} required>
                  <input className={`input-field ${errors.origin_address ? 'input-error' : ''}`}
                    placeholder="4500 S Freeway, Fort Worth, TX 76115"
                    value={form.origin_address} onChange={e => update('origin_address', e.target.value)} />
                </Field>
                <div className="flex justify-center">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                    ↕
                  </div>
                </div>
                <Field label="Destination Address" error={errors.destination_address} required>
                  <input className={`input-field ${errors.destination_address ? 'input-error' : ''}`}
                    placeholder="2800 Cargo Ave, Dallas, TX 75261"
                    value={form.destination_address} onChange={e => update('destination_address', e.target.value)} />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4 max-w-lg">
                <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Departure Date">
                    <input type="date" className="input-field"
                      value={form.scheduled_departure} onChange={e => update('scheduled_departure', e.target.value)} />
                  </Field>
                  <Field label="Departure Time">
                    <input type="time" className="input-field"
                      value={form.scheduled_departure_time} onChange={e => update('scheduled_departure_time', e.target.value)} />
                  </Field>
                  <Field label="Est. Arrival Date">
                    <input type="date" className="input-field"
                      value={form.scheduled_arrival} onChange={e => update('scheduled_arrival', e.target.value)} />
                  </Field>
                  <Field label="Est. Arrival Time">
                    <input type="time" className="input-field"
                      value={form.scheduled_arrival_time} onChange={e => update('scheduled_arrival_time', e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 max-w-lg">
                <h3 className="text-lg font-semibold text-gray-900">Assign Driver & Vehicle</h3>
                <Field label="Driver" error={errors.driver_id}>
                  <select className={`input-field ${errors.driver_id ? 'input-error' : ''}`}
                    value={form.driver_id} onChange={e => update('driver_id', e.target.value)}>
                    <option value="">Select driver (optional)…</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.full_name || `${d.first_name} ${d.last_name}`} — CDL {d.cdl_class} ({d.cdl_state})
                      </option>
                    ))}
                  </select>
                  {drivers.length === 0 && <p className="text-xs text-amber-600 mt-1">No available drivers found.</p>}
                </Field>
                <Field label="Vehicle" error={errors.vehicle_id}>
                  <select className={`input-field ${errors.vehicle_id ? 'input-error' : ''}`}
                    value={form.vehicle_id} onChange={e => update('vehicle_id', e.target.value)}>
                    <option value="">Select vehicle (optional)…</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.unit_number} — {v.year} {v.make} {v.model} ({v.type?.replace(/_/g, ' ')})
                      </option>
                    ))}
                  </select>
                  {vehicles.length === 0 && <p className="text-xs text-amber-600 mt-1">No available vehicles found.</p>}
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 max-w-lg">
                <h3 className="text-lg font-semibold text-gray-900">Cargo Details</h3>
                <Field label="Load Weight (lbs)">
                  <input type="number" min="0" max="80000" className="input-field" placeholder="42000"
                    value={form.load_weight_lbs} onChange={e => update('load_weight_lbs', e.target.value)} />
                </Field>
                <Field label="Cargo Description">
                  <textarea rows={3} className="input-field resize-none"
                    placeholder="Palletized automotive parts — non-hazmat"
                    value={form.cargo_description} onChange={e => update('cargo_description', e.target.value)} />
                </Field>
                <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                  <input type="checkbox" id="hazmat" checked={form.is_hazmat}
                    onChange={e => update('is_hazmat', e.target.checked)}
                    className="w-4 h-4 rounded text-blue-600" />
                  <label htmlFor="hazmat" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Hazardous Materials (HAZMAT)
                  </label>
                </div>
                {form.is_hazmat && (
                  <Field label="HAZMAT Class">
                    <select className="input-field" value={form.hazmat_class} onChange={e => update('hazmat_class', e.target.value)}>
                      <option value="">Select class…</option>
                      {['Class 1 - Explosives','Class 2 - Gases','Class 3 - Flammable Liquids',
                        'Class 4 - Flammable Solids','Class 5 - Oxidizers','Class 6 - Toxic',
                        'Class 7 - Radioactive','Class 8 - Corrosives','Class 9 - Miscellaneous'].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                )}
                <Field label="Special Instructions">
                  <textarea rows={2} className="input-field resize-none"
                    placeholder="Dock door 7. Call receiver 30 min before arrival."
                    value={form.special_instructions} onChange={e => update('special_instructions', e.target.value)} />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-3 max-w-lg">
                <h3 className="text-lg font-semibold text-gray-900">Route Options</h3>
                {[
                  { key: 'avoid_tolls',       label: 'Avoid Tolls',       desc: 'Route via toll-free roads when possible' },
                  { key: 'avoid_ferries',     label: 'Avoid Ferries',     desc: 'Exclude ferry crossings from route' },
                  { key: 'prefer_highways',   label: 'Prefer Highways',   desc: 'Prioritize interstate highways' },
                ].map(opt => (
                  <label key={opt.key} className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={form[opt.key]}
                      onChange={e => update(opt.key, e.target.checked)}
                      className="w-4 h-4 rounded text-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4 max-w-lg">
                <h3 className="text-lg font-semibold text-gray-900">Review & Dispatch</h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  {[
                    { label: 'Origin',      value: form.origin_address },
                    { label: 'Destination', value: form.destination_address },
                    { label: 'Departure',   value: form.scheduled_departure ? `${form.scheduled_departure} ${form.scheduled_departure_time}` : 'Not set' },
                    { label: 'Driver',      value: drivers.find(d => d.id === form.driver_id)?.full_name || 'Unassigned' },
                    { label: 'Vehicle',     value: vehicles.find(v => v.id === form.vehicle_id)?.unit_number || 'Unassigned' },
                    { label: 'Weight',      value: form.load_weight_lbs ? `${parseInt(form.load_weight_lbs).toLocaleString()} lbs` : 'Not specified' },
                    { label: 'HAZMAT',      value: form.is_hazmat ? `Yes — ${form.hazmat_class || 'class unspecified'}` : 'No' },
                  ].map(r => (
                    <div key={r.label} className="flex gap-4">
                      <span className="text-sm text-gray-500 w-28 flex-shrink-0">{r.label}</span>
                      <span className="text-sm font-medium text-gray-900">{r.value}</span>
                    </div>
                  ))}
                  {routeEst && (
                    <>
                      <div className="border-t border-gray-200 my-1" />
                      <div className="flex gap-4">
                        <span className="text-sm text-gray-500 w-28">Est. Distance</span>
                        <span className="text-sm font-medium text-gray-900">{routeEst.distance_miles} miles</span>
                      </div>
                      <div className="flex gap-4">
                        <span className="text-sm text-gray-500 w-28">Est. Fuel</span>
                        <span className="text-sm font-medium text-gray-900">{routeEst.est_fuel_gallons} gallons</span>
                      </div>
                    </>
                  )}
                </div>

                {form.is_hazmat && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <span className="text-amber-500">⚠</span>
                    <p className="text-sm text-amber-700 font-medium">HAZMAT load — ensure driver has proper endorsements</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50/50 flex items-center justify-between">
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-secondary py-2 px-3">Cancel</button>
              {step > 0 && (
                <button onClick={back} disabled={loading} className="btn-secondary py-2 px-3">← Back</button>
              )}
            </div>
            <div className="flex gap-2">
              {step < 5 && (
                <button onClick={next} className="btn-primary">
                  Next Step →
                </button>
              )}
              {step === 5 && (
                <>
                  <button onClick={() => submit(false)} disabled={loading} className="btn-secondary py-2">
                    {loading ? 'Saving…' : 'Save as Draft'}
                  </button>
                  <button onClick={() => submit(true)} disabled={loading || !form.driver_id} className="btn-primary">
                    {loading ? 'Dispatching…' : 'Dispatch Now'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
